import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { UserAuth } from '../context/AuthContext';
import TsLogoWhite from '../assets/TSLogoWhite.svg';
import '../setupapp/setup.css'; // Use the V2 redesign styles

const SignIn = ({ isPlugin = false }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [focusedInput, setFocusedInput] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();

    const { signIn, user } = UserAuth();

    // Store in state to ensure we have it for navigate
    const [capturedMachineId, setCapturedMachineId] = useState(null);
    const [capturedProduct, setCapturedProduct] = useState(null);

    useEffect(() => {
        // Capture parameters even if the reload path was bypassed
        const searchParams = new URLSearchParams(location.search || window.location.search);
        const mId = searchParams.get('machine_id');
        const prod = searchParams.get('product');

        if (mId) {
            localStorage.setItem('plugin_machine_id', mId);
            setCapturedMachineId(mId);
        }
        if (prod) {
            localStorage.setItem('plugin_product', prod);
            setCapturedProduct(prod);
        }
    }, [location.search]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            await signIn(email, password);
            signInAndNavigate();
        } catch (error) {
            setIsLoading(false);
            let friendlyMessage;
            switch (error.code) {
                case 'auth/wrong-password':
                    friendlyMessage = "Incorrect password. Please try again.";
                    break;
                case 'auth/user-not-found':
                    friendlyMessage = "We can't seem to find an account with that email.";
                    break;
                case 'auth/invalid-email':
                    friendlyMessage = "Oops, that doesn't look like a valid email address.";
                    break;
                default:
                    friendlyMessage = "Authentication failed. Please check your connection.";
                    break;
            }
            setError(friendlyMessage);
        }
    }

    const signInAndNavigate = () => {
        if (isPlugin) {
            const product = capturedProduct || localStorage.getItem('plugin_product');
            const machineId = capturedMachineId || localStorage.getItem('plugin_machine_id');
            if (product) {
                let navUrl = `/${product.toLowerCase()}/create/`;
                if (machineId) {
                    navUrl += `?machine_id=${machineId}&product=${product}`;
                }
                navigate(navUrl);
            } else {
                navigate('/aurora/create/');
            }
        } else {
            const pending = sessionStorage.getItem('pendingCheckoutData');
            if (pending) {
                const parsedState = JSON.parse(pending);
                sessionStorage.removeItem('pendingCheckoutData');
                navigate('/checkout', { state: parsedState });
            } else {
                navigate('/aurora');
            }
        }
    }

    return (
        <div className="setup-app-container setup-page-with-header">
            {/* The V2 glass panel */}
            <motion.div
                className="setup-glass-panel"
                initial={{ opacity: 0, y: 40, scale: 0.9, rotateX: 10 }} 
                animate={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
                transition={{ duration: 0.8, type: "spring", bounce: 0.3 }}
                style={{ perspective: 1000 }}
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
                    style={{ textAlign: 'center' }}
                >
                    <img src={TsLogoWhite} alt="Tape Signal" style={{ width: '64px', marginBottom: '24px' }} />
                </motion.div>
                
                <motion.h1 
                    className="setup-header"
                    initial={{ opacity: 0, filter: 'blur(10px)' }} 
                    animate={{ opacity: 1, filter: 'blur(0px)' }} 
                    transition={{ delay: 0.3, duration: 0.8 }}
                >
                    Sign In.
                </motion.h1>
                <motion.p 
                    className="setup-subtitle"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
                >
                    Access your account and plugins.
                </motion.p>

                <motion.form 
                    onSubmit={handleSubmit}
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.5 }}
                >
                    <div className="setup-input-group">
                        <motion.input
                            className="setup-input"
                            type="email"
                            placeholder="Email Address"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onFocus={() => setFocusedInput('email')}
                            onBlur={() => setFocusedInput(null)}
                            required
                        />
                    </div>
                    <div className="setup-input-group">
                        <motion.input
                            className="setup-input"
                            type="password"
                            placeholder="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onFocus={() => setFocusedInput('password')}
                            onBlur={() => setFocusedInput(null)}
                            required
                        />
                    </div>

                    <motion.button
                        type="submit"
                        className="setup-btn-primary"
                        disabled={!email || !password || isLoading}
                        style={{ marginTop: '8px' }}
                    >
                        {isLoading ? <div className="setup-loader-ring"></div> : "Sign In"}
                    </motion.button>

                    <AnimatePresence>
                        {error && (
                            <motion.div
                                className="setup-error"
                                initial={{ opacity: 0, height: 0, marginTop: 0 }}
                                animate={{ opacity: 1, height: 'auto', marginTop: 24 }}
                                exit={{ opacity: 0, height: 0, marginTop: 0 }}
                            >
                                {error}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {!isPlugin && (
                        <motion.div 
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
                            style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px', padding: '0 8px' }}
                        >
                            <button type="button" className="setup-link" onClick={() => navigate('/reset')}>
                                Forgot Password
                            </button>
                            <button type="button" className="setup-link" onClick={() => {
                                const navSlug = new URLSearchParams(window.location.search).get('nav');
                                navigate(navSlug ? `/signup/${navSlug}` : '/signup/aurora');
                            }}>
                                Create Account
                            </button>
                        </motion.div>
                    )}
                </motion.form>
            </motion.div>
        </div>
    );
};

export default SignIn;